import React, { useEffect, useState } from 'react';
import './CodeEditor.css';
import BarChartView from './BarChartView';

const CodeEditor = ({ isLoading, content, searchResults, displayLabel, graphData }) => {
  const [selectedBar, setSelectedBar] = useState(null);
  const [animatedPercent, setAnimatedPercent] = useState(0);

  useEffect(() => {
    if (!selectedBar) return;

    const end = selectedBar.similarity;
    const duration = 1000;
    const startTime = performance.now();

    const easeOutCubic = t => 1 - Math.pow(1 - t, 3);

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      setAnimatedPercent(Math.floor(eased * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [selectedBar]);

  const highlightCode = (code, results) => {
    if (!results || results.length === 0) return code;
    return code.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {results.some(r => r.lineNumber === i + 1)
          ? <span className="highlight">{line}</span>
          : line}
        {'\n'}
      </React.Fragment>
    ));
  };

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - animatedPercent / 100);

  return (
    <div className="code-editor">
      <h2 className="editor-heading">{displayLabel}</h2>

      {isLoading ? (
        <div className="loader-wrapper">
          <div className="loader" />
          <div className="loader-message">Analyzing Code...</div>
        </div>
      ) : selectedBar ? (
        <div className="bar-details-wrapper">
          <div className="bar-details">
            {/* Grid of metric boxes */}
            <div className="info-grid">
              <div className="info-box"><h4>Against</h4><p>{selectedBar.comparingAgainst}</p></div>
              <div className="info-box"><h4>Orig Subtrees</h4><p>{selectedBar.originalSubtrees}</p></div>
              <div className="info-box"><h4>Suspect Subtrees</h4><p>{selectedBar.suspectSubtrees}</p></div>
            </div>
            <div className="info-grid-centered">
              <div className="info-box"><h4>Weighted Score</h4><p>{selectedBar.weightedScore}</p></div>
              <div className="info-box"><h4>Total Score</h4><p>{selectedBar.totalScore}</p></div>
            </div>

            {/* Animated Circular similarity chart */}
            <div className="circular-chart">
              <svg width="120" height="120">
                <circle
                  className="circular-bg"
                  cx="60" cy="60" r={radius}
                  fill="none" stroke="#333" strokeWidth="8"
                />
                <circle
                  className="circular-fg"
                  cx="60" cy="60" r={radius}
                  fill="none" stroke="#57b950" strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  style={{
                    transition: 'stroke-dashoffset 0.9s ease-out',
                    transformOrigin: '50% 50%'
                  }}
                />
              </svg>
              <div className="circular-text">{Math.round(selectedBar.similarity)}%</div>
            </div>

            {selectedBar.similarity >= 85 ? (
              <div className="severity-msg severity-high">
                High Similarity detected!<br />
                This submission is highly likely to be plagiarized.
              </div>
            ) : selectedBar.similarity >= 70 ? (
              <div className="severity-msg severity-likely">
                Suspicious Similarity<br />
                This submission is likely to be plagiarized. Please review carefully.
              </div>
            ) : selectedBar.similarity >= 40 ? (
              <div className="severity-msg severity-med">
                Moderate Similarity<br />
                Some code segments overlap. Further inspection recommended.
              </div>
            ) : (
              <div className="severity-msg severity-low">
                Low Similarity<br />
                No significant overlap detected.
              </div>
            )}

            <button className="button" onClick={() => setSelectedBar(null)}>
              Back to Graph
            </button>
          </div>
        </div>
      ) : graphData?.length > 0 ? (
        <BarChartView
          data={graphData}
          onBarClick={(detail) => {
            setSelectedBar(detail);
            setAnimatedPercent(0);
          }}
        />
      ) : (
        <pre><code className="code">{highlightCode(content, searchResults)}</code></pre>
      )}
    </div>
  );
};

export default CodeEditor;
